/**
 * Foco de audio: en toda la app suena UN audio a la vez.
 *
 * Todo lo que reproduce voz o música — el TTS en vivo de la charla, el replay
 * de una burbuja, el player de Escuchar, una canción, una escena de A0 — pide
 * el foco antes de sonar. Pedirlo DESPLAZA al dueño anterior: se le llama su
 * `stop` y se calla. Gana siempre el último que empezó a sonar, que es el que
 * el usuario acaba de pedir; sin esto dos audios se superponen y no se entiende
 * ninguno.
 *
 * Es un singleton de módulo (mismo criterio que `orbEnergyChannel`): hay una
 * sola salida de audio en el cliente, así que el árbitro es global y evita
 * cablear un contexto por seis capas de UI.
 *
 * Los SFX (`apps/speak/src/lib/sfx`) quedan FUERA a propósito: son blips de
 * interfaz de ~100ms por un AudioContext dedicado, no "un audio" que el usuario
 * esté escuchando. Cortar al coach por el click de un botón sería peor que el
 * solape que este módulo resuelve.
 */

export interface AudioFocusHandle {
  /** `true` mientras este dueño siga teniendo el foco (false si lo desplazaron). */
  readonly active: boolean;
  /** Suelta el foco. Idempotente; no-op si ya lo había perdido. */
  release(): void;
}

interface Holder {
  label: string;
  stop: () => void;
  /** Ya se le pidió callarse (desplazado o liberado): no se le vuelve a llamar. */
  done: boolean;
}

let current: Holder | null = null;

/**
 * Pide el foco de audio para el que está por sonar. `stop` es cómo se calla
 * este dueño cuando otro lo desplace — se llama a lo sumo una vez.
 *
 * Pedirlo NO es opcional ni consultivo: el que pide, gana. La política vive
 * acá, no en cada llamador.
 */
export function requestAudioFocus(label: string, stop: () => void): AudioFocusHandle {
  const holder: Holder = { label, stop, done: false };
  const prev = current;
  // El dueño nuevo queda publicado ANTES de callar al anterior: el `stop` del
  // desplazado normalmente llama a su propio `release()`, y con el orden
  // invertido ese release borraría al dueño que recién entró.
  current = holder;
  if (prev && !prev.done) {
    prev.done = true;
    try {
      prev.stop();
    } catch {
      // Un dueño que falla al callarse no puede romper al que entra.
    }
  }
  return {
    get active(): boolean {
      return current === holder;
    },
    release(): void {
      holder.done = true;
      if (current === holder) current = null;
    },
  };
}

/** Quién tiene el foco ahora (o `null`). Sólo para diagnóstico/tests. */
export function currentAudioFocusLabel(): string | null {
  return current?.label ?? null;
}

/**
 * Ata un `<audio>`/`<video>` al árbitro: toma el foco cuando EMPIEZA A SONAR y
 * lo suelta al pausar/terminar. Devuelve la función de desate (para el cleanup
 * del effect que lo montó).
 *
 * Escucha `playing`, no `play`: `play` es "pedí reproducir" (puede rechazar por
 * política de autoplay o src roto) y con él callaríamos al que sí está sonando
 * para no reproducir nada. `playing` es "ya está saliendo audio" — atar el
 * traspaso a ese evento hace que el solape sea imposible por construcción, sin
 * huecos de silencio.
 *
 * Un elemento MUTEADO no cuenta como "sonando": los videos de presencia en loop
 * (A0) y los `play()` de desbloqueo de autoplay en iOS son mudos, y no tienen
 * por qué callar a lo que el usuario está escuchando.
 */
export function bindMediaAudioFocus(el: HTMLMediaElement, label: string): () => void {
  let handle: AudioFocusHandle | null = null;

  const release = (): void => {
    handle?.release();
    handle = null;
  };

  const claim = (): void => {
    if (el.muted || el.volume === 0) {
      release();
      return;
    }
    if (handle?.active) return;
    handle = requestAudioFocus(label, () => el.pause());
  };

  // Desmutear mientras ya está sonando (A0 pasa de presencia muda a hablar con
  // el mismo elemento) también tiene que tomar el foco.
  const onVolumeChange = (): void => {
    if (el.paused) return;
    claim();
  };

  el.addEventListener('playing', claim);
  el.addEventListener('volumechange', onVolumeChange);
  el.addEventListener('pause', release);
  el.addEventListener('ended', release);

  return () => {
    el.removeEventListener('playing', claim);
    el.removeEventListener('volumechange', onVolumeChange);
    el.removeEventListener('pause', release);
    el.removeEventListener('ended', release);
    release();
  };
}
